import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as FaIcons from 'react-icons/fa';
import './ProductFilters.css';
import { ProductFiltersProps, ProductType } from '../types';
import { productTypeGenitive, productTypeLabel } from '../utils/productType';
import axios from 'axios';

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Update your interface definition
interface FilterFields {
  name: string;
  brand: string;
  country: string;
  region: string;
  product_type_id: string;
  productType?: string;
  category?: string;
  category_id: string;
  subcategory_id: string;
  grape_variety: string;
  vintage: string;
  owner?: string;
  characteristics: string;
  features: string; // Add this line
  [key: string]: string | undefined; // Index signature
}

// Add this interface near the top of the file with your other interfaces
interface SubcategoryOption {
  id: number | string;
  name: string;
}


const ProductFilters: React.FC<ProductFiltersProps> = ({ 
  filters, 
  onChange, 
  onProductTypeChange,
  onPriceChange,
  onPriceBlur,
  onPriceKeyDown,
  brandOptions = [],
  categoryOptions = [],
  countryOptions = [],
  regionOptions = [],
  vintageOptions = [],
  userOptions = [],
  className = '',
  productTypes = [],
  resetFilters = () => {},
  resultCount,
  totalCount,
  showAdvancedFilters,
  toggleAdvancedFilters
}) => {
  // The advanced block used to be rendered unconditionally, so the panel showed
  // every control whether it was set or not and ate a third of the screen before
  // you had seen a single product. Callers may drive this, but the component
  // manages it on its own when they do not.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const isAdvancedOpen = showAdvancedFilters ?? advancedOpen;
  const handleToggleAdvanced = toggleAdvancedFilters ?? (() => setAdvancedOpen((open) => !open));

  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  
  // REMOVE all the local state for filters. It's causing conflicts.
  // const [localFilters, setLocalFilters] = useState<FilterFields>(filters as FilterFields);
  
  // New states for feature options
  const [featureOptions, setFeatureOptions] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  
  // Add these new states near your other state declarations
  const [searchInput, setSearchInput] = useState(filters.name || '');
  const [searchError, setSearchError] = useState(false);

  // Debounce search input to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchInput, 500); // Increased delay to 500ms for better user experience

  // New function to fetch available features - This is correct.
  const fetchFeatures = useCallback(async (productTypeId: string) => {
    if (!productTypeId || productTypeId === 'all' || productTypeId === '') {
      setFeatureOptions([]);
      return;
    }

    try {
      setLoadingFeatures(true);
      const response = await axios.get(`/api/features/options?product_type_id=${productTypeId}`);
      setFeatureOptions(response.data || []);
    } catch (err) {
      console.error('Error fetching features:', err);
      setFeatureOptions([]);
    } finally {
      setLoadingFeatures(false);
    }
  }, []);

  // CONSOLIDATED useEffect for product type changes.
  // This replaces the two separate useEffects you had before.
  useEffect(() => {
    const fetchCategoriesForEffect = async (productTypeId: string) => {
      const numericId = parseInt(productTypeId, 10);
      if (isNaN(numericId)) {
        setCategories([]);
        return;
      }
      setLoadingCategories(true);
      try {
        const response = await axios.get(`/api/categories?product_type_id=${numericId}`);
        setCategories(response.data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    if (filters.product_type_id) {
      fetchCategoriesForEffect(filters.product_type_id);
      fetchFeatures(filters.product_type_id); // Fetch features here as well
    } else {
      // When product type is cleared, reset all dependent states
      setCategories([]);
      setSubcategories([]);
      setFeatureOptions([]);
    }
  }, [filters.product_type_id, fetchFeatures]); // Add fetchFeatures to the dependency array


  // This is the corrected, robust useEffect for fetching subcategories.
  useEffect(() => {
    const fetchSubcategories = async (categoryId: string) => {
      const numericId = parseInt(categoryId, 10);
      if (isNaN(numericId)) {
        setSubcategories([]);
        return;
      }
      setLoadingSubcategories(true);
      try {
        const response = await axios.get(`/api/subcategories?category_id=${numericId}`);
        setSubcategories(response.data || []);
      } catch (err) {
        console.error('Error fetching subcategories:', err);
        setSubcategories([]);
      } finally {
        setLoadingSubcategories(false);
      }
    };

    if (filters.category_id) {
      fetchSubcategories(filters.category_id);
    } else {
      // If category_id is cleared, we must clear subcategories.
      setSubcategories([]);
    }
  }, [filters.category_id]); // This effect now correctly depends ONLY on the category ID.

  // Handle product type button clicks - this should IMMEDIATELY clear the UI
  const handleProductTypeButtonClick = useCallback((typeId: string) => {
    console.log('ProductFilters: switching to type', typeId); // Debug log
    
    // Clear local state immediately
    setCategories([]);
    setSubcategories([]);
    setFeatureOptions([]);
    
    // Tell parent to switch context (this will clear filters)
    onProductTypeChange(typeId);
  }, [onProductTypeChange]);

  // SIMPLIFY the change handler. It should just pass the change up to the parent.
  const handleFilterChange = (field: string, value: string) => {
    onChange(field, value);
  };

  // Add this new function here
  const handleFeatureToggle = useCallback((feature: string) => {
    const currentFeatures = filters.features ? filters.features.split(',') : [];
    const newFeatures = currentFeatures.includes(feature)
      ? currentFeatures.filter(f => f !== feature)
      : [...currentFeatures, feature];
    
    onChange('features', newFeatures.join(','));
  }, [filters.features, onChange]);

  // Get filter options specific to current product type
  const getFilteredOptions = useCallback((allOptions: string[], productTypeId: string) => {
    // You can implement logic here to show only relevant options per product type
    // For now, return all options but in production you might want to filter
    return allOptions;
  }, []);

  const isRelevantForProductType = useCallback((option: string, productTypeId: string): boolean => {
    // This function can filter options based on product type
    // For now, return true for all options, but you can add logic here
    // to show only relevant countries/brands for specific product types
    return true;
  }, []);

  // Memoize options to prevent unnecessary re-renders
  const dynamicCategoryOptions = useMemo(() => 
    categories.length > 0 ? categories : 
    (categoryOptions.length > 0 ? categoryOptions : [])
  , [categories, categoryOptions]);

  // REMOVE the useMemo for subcategoryOptions. It's causing the issue.
  // const subcategoryOptions = useMemo(() => ... ); // DELETE THIS LINE

  // Both of these read from utils/productType, so the filter panel, the card
  // grid and the table all label a product type the same way.
  const getProductTypeName = useCallback(
    (typeId: string) => productTypeGenitive(typeId, productTypes),
    [productTypes]
  );

  const getProductTypeDisplayName = useCallback(
    (typeId: string) => productTypeLabel(typeId, productTypes),
    [productTypes]
  );

  // Add local state for form values to prevent immediate updates
  const [localFilters, setLocalFilters] = useState<FilterFields>(filters as FilterFields);
  
  // Update local filters when props change
  useEffect(() => {
    // ONLY update localFilters if we're switching product types or initial load
    // Don't update for individual field changes
    if (!localFilters.product_type_id || localFilters.product_type_id !== filters.product_type_id) {
      setLocalFilters(filters as FilterFields);
    }
  }, [filters.product_type_id]); // Only depend on product_type_id changes
  
  // Handle local filter changes without updating parent
  const handleLocalFilterChange = (field: string, value: string) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };
  
  // Add this function to ensure the state persists after filter changes
  const handleAdvancedFilterChange = (field: string, value: string) => {
    // Update the local filter state
    setLocalFilters(prev => ({ ...prev, [field]: value }));
    
    // Make the API call with the updated filter
    onChange(field, value);
    
    // Important: we don't toggle the showMoreFilters state here
    // This ensures the advanced filters section stays open
  };

  // Apply filters on blur or submit
  const applyFilters = () => {
    console.log('Applying filters:', localFilters); // Debug
    
    // Update ALL changed fields, ensuring text inputs are included
    Object.entries(localFilters).forEach(([field, value]) => {
      const currentValue = filters[field as keyof typeof filters];
      if (value !== currentValue) {
        console.log(`Updating ${field} from "${currentValue}" to "${value}"`);
        onChange(field, value || '');
      }
    });
    
    // DON'T try to restore showMoreFilters state - let it stay as is
  };
  
  // Add states for autocomplete suggestions
  const [regionSuggestions, setRegionSuggestions] = useState<string[]>([]);
  const [grapeSuggestions, setGrapeSuggestions] = useState<string[]>([]);
  const [characteristicsSuggestions, setCharacteristicsSuggestions] = useState<string[]>([]);
  const [showRegionSuggestions, setShowRegionSuggestions] = useState(false);
  const [showGrapeSuggestions, setShowGrapeSuggestions] = useState(false);
  const [showCharacteristicsSuggestions, setShowCharacteristicsSuggestions] = useState(false);

  // Add state for the grape variety tag input
  const [grapeInput, setGrapeInput] = useState('');

  // Function to fetch suggestions from backend
  const fetchSuggestions = useCallback(async (field: string, query: string) => {
    if (!query || query.length < 2) return [];
    
    try {
      const response = await axios.get(`/api/suggestions?field=${field}&query=${encodeURIComponent(query)}`);
      return response.data || [];
    } catch (err) {
      console.error(`Error fetching ${field} suggestions:`, err);
      return [];
    }
  }, []);

  // Handlers for autocomplete
  const handleRegionInputChange = useCallback(async (value: string) => {
    handleLocalFilterChange('region', value); // Only update local state
    if (value.length >= 2) {
      const suggestions = await fetchSuggestions('region', value);
      setRegionSuggestions(suggestions);
      setShowRegionSuggestions(suggestions.length > 0);
    } else {
      setShowRegionSuggestions(false);
    }
  }, [fetchSuggestions]);

  // This function now just updates the local input state and fetches suggestions
  const handleGrapeInputChange = useCallback(async (value: string) => {
    setGrapeInput(value);
    if (value.length >= 2) {
      const suggestions = await fetchSuggestions('grape_variety', value);
      setGrapeSuggestions(suggestions);
      setShowGrapeSuggestions(suggestions.length > 0);
    } else {
      setShowGrapeSuggestions(false);
    }
  }, [fetchSuggestions]);

  // New handler to add a grape variety tag
  const handleAddGrapeTag = (grapeToAdd: string) => {
    if (!grapeToAdd || grapeToAdd.trim() === '') return;

    const trimmedGrape = grapeToAdd.trim();
    const currentGrapes = filters.grape_variety ? filters.grape_variety.split(',') : [];

    // Case-insensitive check to prevent adding duplicate tags
    if (!currentGrapes.map(g => g.toLowerCase()).includes(trimmedGrape.toLowerCase())) {
      const newGrapes = [...currentGrapes, trimmedGrape];
      onChange('grape_variety', newGrapes.join(','));
    }

    // Clear the input field and hide suggestions
    setGrapeInput('');
    setShowGrapeSuggestions(false);
  };

  // New handler to remove a grape variety tag
  const handleRemoveGrapeTag = (grapeToRemove: string) => {
    const currentGrapes = filters.grape_variety ? filters.grape_variety.split(',') : [];
    const newGrapes = currentGrapes.filter(g => g !== grapeToRemove);
    onChange('grape_variety', newGrapes.join(','));
  };

  const handleCharacteristicsInputChange = useCallback(async (value: string) => {
    handleLocalFilterChange('characteristics', value); // Only update local state
    if (value.length >= 2) {
      const suggestions = await fetchSuggestions('characteristics', value);
      setCharacteristicsSuggestions(suggestions);
      setShowCharacteristicsSuggestions(suggestions.length > 0);
    } else {
      setShowCharacteristicsSuggestions(false);
    }
  }, [fetchSuggestions]);

  // Add refs near the top of the component
  const regionInputRef = React.useRef<HTMLInputElement>(null);
  const grapeInputRef = React.useRef<HTMLInputElement>(null);
  const characteristicsInputRef = React.useRef<HTMLInputElement>(null);

  // REMOVE the complex useEffect that syncs `filters` to `localFilters`.
  // It's the source of the race condition.
  /*
  useEffect(() => {
    // ... a lot of complex logic ...
  }, [filters]);
  */

  // REPLACE it with this much simpler version that only syncs when the parent forces a change
  // (like a product type switch or a full reset).
  useEffect(() => {
    setLocalFilters(filters as FilterFields);
  }, [filters]);


  // FINAL, SIMPLIFIED VERSION of selectSuggestion.
  const selectSuggestion = useCallback((field: string, value: string) => {
    // 1. Update the local state so the input field shows the full value immediately.
    setLocalFilters(prev => ({ ...prev, [field]: value }));

    // 2. Directly call the parent's onChange handler to update the global state.
    // This is the key change to prevent the state from being overwritten.
    onChange(field, value);

    // 3. Hide the suggestions dropdown for the corresponding field.
    if (field === 'region') {
      setShowRegionSuggestions(false);
    } else if (field === 'grape_variety') {
      setShowGrapeSuggestions(false);
    } else if (field === 'characteristics') {
      setShowCharacteristicsSuggestions(false);
    }
  }, [onChange]);


  // In the onBlur handlers for your inputs, we just need to call the parent `onChange`.
  // Example for the Region input:
  // onBlur={() => {
  //   // Hide suggestions after a delay to allow for click events.
  //   setTimeout(() => setShowRegionSuggestions(false), 150);
  //   
  //   // Send the final, local value to the parent when the user leaves the field.
  //   if (filters.region !== localFilters.region) {
  //     onChange('region', localFilters.region || '');
  //   }
  // }}
  // Repeat for other inputs...

  // Use the debounced value to trigger the actual filter change
  useEffect(() => {
    // Only update parent component state (and trigger API call) when debounced value changes
    if (debouncedSearchTerm !== filters.name) {
      onChange('name', debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onChange, filters.name]);

  /* Which filters are actually narrowing the list. Previously the only hint was
     a small green dot on the active type button, so there was no way to see at a
     glance what was filtering the view -- or to drop one filter without opening
     the panel and hunting for the control that set it. */
  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; value: string; clear: () => void }[] = [];

    const add = (key: string, label: string, value?: string, clear?: () => void) => {
      if (!value || value === 'all') return;
      chips.push({ key, label, value, clear: clear ?? (() => onChange(key, '')) });
    };

    add('name', 'Nazwa', filters.name, () => {
      setSearchInput('');
      onChange('name', '');
    });
    add('brand', 'Marka', filters.brand);
    add('country', 'Kraj', filters.country);
    add('region', 'Region', filters.region);
    add('vintage', 'Rocznik', filters.vintage);
    add('grape_variety', 'Szczep', filters.grape_variety);
    add('characteristics', 'Charakterystyka', filters.characteristics);
    add('features', 'Cechy', filters.features);

    const range = (
      key: string,
      label: string,
      min?: string,
      max?: string,
      unit = ''
    ) => {
      if (!min && !max) return;
      const text = min && max ? `${min}–${max}${unit}` : min ? `od ${min}${unit}` : `do ${max}${unit}`;
      chips.push({
        key,
        label,
        value: text,
        clear: () => {
          onChange(`${key}Min`, '');
          onChange(`${key}Max`, '');
        },
      });
    };

    range('price', 'Cena', filters.priceMin, filters.priceMax, ' zł');
    range('alcohol', 'Alkohol', filters.alcoholMin, filters.alcoholMax, '%');

    return chips;
  }, [filters, onChange]);

  return (
    <div className={`product-filters-container ${className}`}>
      {/* Product type selector - this switches filter contexts */}
      <div className="filter-type-selector">
        <button 
          className={`filter-type-btn ${!filters.product_type_id ? 'active' : ''}`}
          onClick={() => handleProductTypeButtonClick('')}
        >
          Wszystkie
        </button>
        {productTypes.map(type => (
          <button 
            key={type.id}
            className={`filter-type-btn ${filters.product_type_id === type.id.toString() ? 'active' : ''}`}
            onClick={() => handleProductTypeButtonClick(type.id.toString())}
          >
            {getProductTypeDisplayName(type.id.toString())}
          </button>
        ))}
      </div>
      
      {/* Search, the advanced toggle and the result count share one toolbar row. */}
      <div className="filter-toolbar">
        <div className="search-container">
        {FaIcons.FaSearch({ className: "search-icon" })}
        <input
          type="text"
          placeholder={`Szukaj ${filters.product_type_id ? getProductTypeName(filters.product_type_id) : 'produktów'}...`}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setSearchError(false);
          }}
          className={`search-input ${searchError ? 'search-error' : ''}`}
        />
        {searchInput && (
          <button 
            className="clear-btn"
            onClick={() => {
              setSearchInput('');
              setSearchError(false);
              onChange('name', '');
            }}
            aria-label="Clear search"
          >
            {FaIcons.FaTimes({})}
          </button>
        )}
        {searchError && (
          <div className="search-error-message">
            Problem z wyszukiwaniem. Spróbuj uprościć zapytanie.
          </div>
        )}
        </div>

        <button
          type="button"
          className="more-filters-btn"
          aria-expanded={isAdvancedOpen}
          onClick={handleToggleAdvanced}
        >
          {isAdvancedOpen ? 'Mniej filtrów' : 'Więcej filtrów'}
        </button>

        {resultCount !== undefined && (
          <span className="filter-result-count">
            {/* "Znaleziono" avoids Polish number agreement, which would need
                three forms (1 produkt / 2 produkty / 5 produktów). */}
            {totalCount !== undefined
              ? `Znaleziono: ${resultCount} z ${totalCount}`
              : `Znaleziono: ${resultCount}`}
          </span>
        )}
      </div>

      {activeChips.length > 0 && (
        <div className="active-filters">
          {activeChips.map((chip) => (
            <span key={chip.key} className="filter-tag">
              {chip.label} <strong>{chip.value}</strong>
              <button
                type="button"
                className="remove-filter"
                onClick={chip.clear}
                aria-label={`Usuń filtr: ${chip.label}`}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className="clear-all-filters" onClick={() => resetFilters()}>
            Wyczyść wszystkie
          </button>
        </div>
      )}
      
      {/* Filters that are specific to current product type */}
      {isAdvancedOpen && (
      <div className="filter-row">
        <div className="filter-select-wrapper">
          <select
            value={filters.brand || ''}
            onChange={(e) => onChange('brand', e.target.value)}
            className="filter-select"
          >
            <option value="">Wszystkie marki</option>
            {brandOptions
              .filter(brand => isRelevantForProductType(brand, filters.product_type_id))
              .map((brand: string) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
          </select>
        </div>
        
        <div className="filter-select-wrapper">
          <select
            value={filters.country || ''}
            onChange={(e) => {
              console.log('Country filter changed to:', e.target.value); // Debug log
              onChange('country', e.target.value);
            }}
            className="filter-select"
          >
            <option value="">Wszystkie kraje</option>
            {getFilteredOptions(countryOptions, filters.product_type_id).map((country: string) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-select-wrapper">
          <select
            value={filters.category_id || ''}
            onChange={(e) => handleFilterChange('category_id', e.target.value)}
            className="filter-select"
            disabled={loadingCategories || !filters.product_type_id}
          >
            <option value="">
              {loadingCategories ? 'Ładowanie...' : 'Wybierz rodzaj'}
            </option>
            {dynamicCategoryOptions.map((cat: any) => (
              <option key={cat.id} value={cat.id.toString()}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-select-wrapper">
          <select
            value={filters.subcategory_id || ''}
            onChange={(e) => handleFilterChange('subcategory_id', e.target.value)}
            className="filter-select"
            disabled={loadingSubcategories || !filters.category_id}
          >
            <option value="">
              {loadingSubcategories 
                ? 'Ładowanie...' 
                : 'Wybierz podkategorię'}
            </option>
            {/* Use the 'subcategories' state directly here */}
            {subcategories.length > 0 ? (
              subcategories.map((subcat: SubcategoryOption) => (
                <option key={subcat.id} value={subcat.id.toString()}>
                  {subcat.name || 'Bez nazwy'}
                </option>
              ))
            ) : (
              // Only show this if not loading and the parent category is selected
              !loadingSubcategories && filters.category_id && (
                <option value="" disabled>Brak podkategorii</option>
              )
            )}
          </select>
        </div>
      </div>
      )}

      {/* Advanced filters */}
      {isAdvancedOpen && (
      <div className="advanced-filters">
        <div className="filter-row">
          <div className="filter-field">
            <label>Region</label>
            <div className="autocomplete-wrapper">
              <input
                ref={regionInputRef}
                type="text"
                placeholder="Np. Bordeaux, Toskania, Mendoza"
                value={localFilters.region || ''}
                onChange={(e) => handleRegionInputChange(e.target.value)}
                onBlur={() => {
                  // Hide suggestions after a delay to allow for click events.
                  setTimeout(() => setShowRegionSuggestions(false), 150);
                }}
                onFocus={() => localFilters.region?.length >= 2 && setShowRegionSuggestions(true)}
                className="filter-input"
              />
              {showRegionSuggestions && (
                <ul className="suggestions-dropdown">
                  {regionSuggestions.map((suggestion, index) => (
                    <li 
                      key={index} 
                      onClick={() => selectSuggestion('region', suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          
          <div className="filter-field">
            <label>Szczep</label>
            <div className="autocomplete-wrapper">
              <input
                ref={grapeInputRef}
                type="text"
                placeholder="Wpisz i naciśnij Enter..."
                value={grapeInput}
                onChange={(e) => handleGrapeInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGrapeTag(grapeInput);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowGrapeSuggestions(false), 150);
                }}
                className="filter-input"
              />
              {showGrapeSuggestions && (
                <ul className="suggestions-dropdown">
                  {grapeSuggestions.map((suggestion, index) => (
                    <li 
                      key={index} 
                      onClick={() => handleAddGrapeTag(suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* This div will display the selected grape tags */}
            <div className="selected-tags">
              {(filters.grape_variety || '').split(',').filter(g => g).map(grape => (
                <span key={grape} className="tag">
                  {grape}
                  <button onClick={() => handleRemoveGrapeTag(grape)} className="remove-tag-btn">×</button>
                </span>
              ))}
            </div>
          </div>
          
          <div className="filter-field">
            <label>Rocznik</label>
            <select
              value={filters.vintage || ''}
              onChange={(e) => handleFilterChange('vintage', e.target.value)}
              className="filter-select"
            >
              <option value="">Wszystkie roczniki</option>
              {vintageOptions.map((year: number) => (
                <option key={year} value={year.toString()}>{year}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-field">
            <label>Cechy</label>
            <div className="autocomplete-wrapper">
              <input
                ref={characteristicsInputRef}
                type="text"
                placeholder="Np. organiczne, biodynamiczne"
                value={localFilters.characteristics || ''}
                onChange={(e) => handleCharacteristicsInputChange(e.target.value)}
                onBlur={() => {
                  setTimeout(() => setShowCharacteristicsSuggestions(false), 150);
                }}
                onFocus={() => localFilters.characteristics?.length >= 2 && setShowCharacteristicsSuggestions(true)}
                className="filter-input"
              />
              {showCharacteristicsSuggestions && (
                <ul className="suggestions-dropdown">
                  {characteristicsSuggestions.map((suggestion, index) => (
                    <li 
                      key={index} 
                      onClick={() => selectSuggestion('characteristics', suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        
        {/* New section for feature filters */}
        <div className="filter-section">
          <h4>Cechy charakterystyczne</h4>
          {loadingFeatures ? (
            <div className="loading-features">Ładowanie...</div>
          ) : featureOptions.length === 0 ? (
            <div className="no-features">Brak dostępnych cech dla wybranego typu produktu</div>
          ) : (
            <div className="features-grid">
              {featureOptions.map(feature => {
                const isChecked = filters.features ? filters.features.split(',').includes(feature) : false;
                
                return (
                  <div key={feature} className="feature-checkbox">
                    <label className={`feature-label ${isChecked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleFeatureToggle(feature)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="feature-name">{feature}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="filter-row">
          <div className="filter-field">
            <label>Cena min (netto PLN)</label>
            <input
              type="number"
              placeholder="0"
              value={filters.priceMin || ''}
              onChange={(e) => {
                if (onPriceChange) {
                  onPriceChange('priceMin', e.target.value);
                } else {
                  onChange('priceMin', e.target.value);
                }
              }}
              onBlur={() => onPriceBlur && onPriceBlur('priceMin')}
              onKeyDown={(e) => onPriceKeyDown && onPriceKeyDown(e, 'priceMin')}
              className="filter-input"
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="filter-field">
            <label>Cena max (netto PLN)</label>
            <input
              type="number"
              placeholder="9999"
              value={filters.priceMax || ''}
              onChange={(e) => {
                if (onPriceChange) {
                  onPriceChange('priceMax', e.target.value);
                } else {
                  onChange('priceMax', e.target.value);
                }
              }}
              onBlur={() => onPriceBlur && onPriceBlur('priceMax')}
              onKeyDown={(e) => onPriceKeyDown && onPriceKeyDown(e, 'priceMax')}
              className="filter-input"
              min="0"
              step="0.01"
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ProductFilters;